import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import standardPoolApi from '../../services/standardPoolApi';
import {
  setPoolSiteSession,
  getStoredPoolSiteToken,
  getStoredPoolSitePoolId,
} from '../../utils/poolSiteAuth';
import axios from '../../utils/axios';
import PoolSiteSafetyAlertDialog from '../pool/PoolSiteSafetyAlertDialog';

interface PoolAccessGuardProps {
  children: React.ReactNode;
}

const PoolAccessGuard: React.FC<PoolAccessGuardProps> = ({ children }) => {
  const { poolCode } = useParams<{ poolCode: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { authState } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  /** 항상 URL poolCode 기준 ID — 이전 시설 세션 ID를 그대로 쓰지 않음 */
  const [resolvedPoolId, setResolvedPoolId] = useState<number | null>(null);

  useEffect(() => {
    if (!poolCode) {
      setResolvedPoolId(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await axios.get(`/api/pools/code/${poolCode}`);
        const id = Number(response?.data?.id);
        if (!cancelled && Number.isFinite(id) && id > 0) {
          setResolvedPoolId(id);
        }
      } catch {
        if (!cancelled) {
          // 코드 조회 실패 시에만, 현재 URL 시설과 세션이 일치할 때 세션 ID 사용
          const stored = getStoredPoolSitePoolId();
          if (stored) setResolvedPoolId(stored);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poolCode]);

  useEffect(() => {
    const checkAccess = async () => {
      if (authState.isAdminPasswordVerified && authState.isAuthenticated) {
        return;
      }

      if (!poolCode) {
        enqueueSnackbar('시설 코드 필요', { variant: 'error' });
        navigate('/pool-management');
        return;
      }

      const authKey = `pool_auth_${poolCode}`;
      let isAuthorized = localStorage.getItem(authKey) === 'true';

      if (isAuthorized && !getStoredPoolSiteToken()) {
        localStorage.removeItem(authKey);
        isAuthorized = false;
      }

      // 다른 시설 JWT가 남아 있으면 이 URL에서는 미인증으로 처리
      if (isAuthorized && resolvedPoolId != null) {
        const storedPid = getStoredPoolSitePoolId();
        if (storedPid != null && storedPid !== resolvedPoolId) {
          localStorage.removeItem(authKey);
          isAuthorized = false;
        }
      }

      if (isAuthorized) {
        return;
      }

      setIsDialogOpen(true);
    };
    void checkAccess();
  }, [
    poolCode,
    resolvedPoolId,
    authState.isAdminPasswordVerified,
    authState.isAuthenticated,
    enqueueSnackbar,
    navigate,
  ]);

  const handleVerify = async () => {
    if (!password) {
      setError('비밀번호를 입력');
      return;
    }

    try {
      setIsVerifying(true);
      setError('');

      const response = await axios.get(`/api/pools/code/${poolCode}`);

      if (!response.data || !response.data.id) {
        throw new Error('풀을 찾기 불가');
      }

      const poolId = response.data.id;
      setResolvedPoolId(Number(poolId));

      const verifyResponse = await standardPoolApi.verifyPoolPasswordById(poolId, password);

      if (!verifyResponse.success) {
        throw new Error(verifyResponse.message || '비밀번호 검증에 실패');
      }

      const token = verifyResponse.data?.access_token;
      if (token) {
        setPoolSiteSession(token, poolId);
      }

      const authKey = `pool_auth_${poolCode}`;
      localStorage.setItem(authKey, 'true');
      setIsDialogOpen(false);
      enqueueSnackbar('인증됨', { variant: 'success' });
    } catch (err) {
      setError(err instanceof Error ? err.message : '비밀번호 검증 중 오류 발생');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <>
      {children}
      <PoolSiteSafetyAlertDialog poolId={resolvedPoolId} />
      {!authState.isAdminPasswordVerified || !authState.isAuthenticated ? (
        <Dialog open={isDialogOpen} onClose={() => {}}>
          <DialogTitle>시설 접근 인증</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                {poolCode} 시설 접근. 비밀번호 입력
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="비밀번호"
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    void handleVerify();
                  }
                }}
              />
              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => navigate('/pool-management')} color="inherit">
              취소
            </Button>
            <Button
              onClick={() => void handleVerify()}
              color="primary"
              disabled={isVerifying || !password}
            >
              {isVerifying ? '확인 중...' : '확인'}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </>
  );
};

export default PoolAccessGuard;
